import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { DataTable } from '@/components/ui/data-table'
import { StockSummaryCards } from '@/components/stock/stock-summary-cards'
import { VelocityHeatmap } from '@/components/stock/velocity-heatmap'
import { UnitsByBranchChart } from '@/components/stock/units-by-branch-chart'
import { DivergenceChart } from '@/components/stock/divergence-chart'
import { StockoutMonthChart } from '@/components/stock/stockout-month-chart'
import { StockToolbar, type StockDatePreset, type StockTypeFilter } from '@/components/stock/stock-toolbar'
import { getOpportunityColumns } from '@/components/stock/opportunity-columns'
import { getStockoutColumns } from '@/components/stock/stockout-columns'
import { SkuSheet } from '@/components/stock/sku-sheet'
import { useStockOverview } from '@/hooks/useStock'
import { useBranch, type BranchId } from '@/providers/branch-provider'
import { ArrowLeftRight, AlertTriangle, Info, LayoutGrid } from 'lucide-react'

export const Route = createFileRoute('/stock')({
  component: StockPage,
})

const BRANCH_IDS: Record<BranchId, number | undefined> = {
  all: undefined,
  'shah-alam': 1,
  putrajaya: 2,
  bangi: 3,
  'kuala-lumpur': 4,
}

type StockTab = 'overview' | 'rebalance' | 'stockouts'
const TABS: { value: StockTab; label: string; icon: typeof LayoutGrid }[] = [
  { value: 'overview', label: 'Overview', icon: LayoutGrid },
  { value: 'rebalance', label: 'Rebalance', icon: ArrowLeftRight },
  { value: 'stockouts', label: 'Stockouts', icon: AlertTriangle },
]

function ymd(d: Date) {
  return d.toLocaleDateString('en-CA')
}
function presetToRange(preset: StockDatePreset, from: string, to: string): { from?: string; to?: string } {
  if (preset === 'custom') return { from: from || undefined, to: to || undefined }
  const myt = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kuala_Lumpur' }))
  const end = ymd(myt)
  if (preset === 'ytd') return { from: `${myt.getFullYear()}-01-01`, to: end }
  const days = preset === 'last30' ? 29 : preset === 'last180' ? 179 : 89
  const start = new Date(myt)
  start.setDate(start.getDate() - days)
  return { from: ymd(start), to: end }
}

function StockPage() {
  const { branchId: branchKey } = useBranch()
  const branchId = BRANCH_IDS[branchKey]

  const [tab, setTab] = React.useState<StockTab>('overview')
  const [datePreset, setDatePreset] = React.useState<StockDatePreset>('last90')
  const [customFrom, setCustomFrom] = React.useState('')
  const [customTo, setCustomTo] = React.useState('')
  const [type, setType] = React.useState<StockTypeFilter>('all')
  const [coverageDays, setCoverageDays] = React.useState(45)
  const [demo, setDemo] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [selectedSku, setSelectedSku] = React.useState<number | null>(null)
  const [sheetOpen, setSheetOpen] = React.useState(false)

  const range = presetToRange(datePreset, customFrom, customTo)
  const { data, isLoading } = useStockOverview({
    from: range.from,
    to: range.to,
    type,
    branchId,
    coverageDays,
    minRatio: 3,
    months: 6,
  })

  const mask = React.useCallback(
    (skuId: number, name: string) => (demo ? `SKU-${String(skuId).padStart(3, '0')}` : name),
    [demo],
  )
  const matchSearch = React.useCallback(
    (name: string) => !search.trim() || name.toLowerCase().includes(search.trim().toLowerCase()),
    [search],
  )

  const velocity = React.useMemo(() => (data?.velocity ?? []).filter((v) => matchSearch(v.sku)), [data, matchSearch])
  const opportunities = React.useMemo(() => (data?.opportunities ?? []).filter((o) => matchSearch(o.sku)), [data, matchSearch])
  const stockouts = React.useMemo(() => (data?.stockouts ?? []).filter((s) => matchSearch(s.sku)), [data, matchSearch])

  const oppColumns = React.useMemo(() => getOpportunityColumns({ demo, mask }), [demo, mask])
  const stockoutColumns = React.useMemo(() => getStockoutColumns({ demo, mask }), [demo, mask])

  const openSku = (skuId: number) => {
    setSelectedSku(skuId)
    setSheetOpen(true)
  }

  const tabCounts: Record<StockTab, number | null> = {
    overview: null,
    rebalance: data?.opportunities.length ?? null,
    stockouts: data?.stockouts.length ?? null,
  }

  return (
    <div className="flex flex-col h-full">
      <Header title="Stock Balancing" subtitle="Cross-branch inventory velocity, redistribution & stockout signals" />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
        <StockToolbar
          search={search}
          onSearchChange={setSearch}
          datePreset={datePreset}
          onDatePresetChange={setDatePreset}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          type={type}
          onTypeChange={setType}
          coverageDays={coverageDays}
          onCoverageDaysChange={setCoverageDays}
          demo={demo}
          onDemoChange={setDemo}
        />

        {/* Pill tabs */}
        <div className="inline-flex rounded-full border border-border bg-card p-0.5">
          {TABS.map((t) => {
            const Icon = t.icon
            const active = tab === t.value
            const count = tabCounts[t.value]
            return (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                  active ? 'bg-info text-white' : 'text-text-muted hover:text-text-dark'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
                {count != null && (
                  <span className={`rounded-full px-1.5 text-[10px] ${active ? 'bg-white/25' : 'bg-hover text-text-muted'}`}>{count}</span>
                )}
              </button>
            )
          })}
        </div>

        {/* ===== OVERVIEW ===== */}
        {tab === 'overview' && (
          <div className="space-y-5">
            <StockSummaryCards data={data} isLoading={isLoading} demo={demo} />
            <VelocityHeatmap velocity={velocity} demo={demo} mask={mask} onSelectSku={openSku} />
            <UnitsByBranchChart velocity={velocity} />
          </div>
        )}

        {/* ===== REBALANCE ===== */}
        {tab === 'rebalance' && (
          <div className="space-y-4">
            <DivergenceChart opportunities={opportunities} mask={mask} />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ArrowLeftRight className="w-4 h-4 text-info" />
                <h3 className="text-sm font-semibold text-text-dark">Redistribution opportunities</h3>
                <span className="text-[11px] text-text-muted">same SKU, one branch burns ≥3× another</span>
              </div>
              <DataTable columns={oppColumns} data={opportunities} isLoading={isLoading} onRowClick={(row) => openSku(row.skuId)} />
              <p className="flex items-start gap-1.5 text-[11px] text-text-muted">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>* Suggested move is illustrative — assumes even stocking across branches. Exact quantities resolve once a one-time stock-take seeds on-hand.</span>
              </p>
            </div>
          </div>
        )}

        {/* ===== STOCKOUTS ===== */}
        {tab === 'stockouts' && (
          <div className="space-y-4">
            <StockoutMonthChart stockouts={stockouts} />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-warning" />
                <h3 className="text-sm font-semibold text-text-dark">Stockout signals</h3>
                <span className="text-[11px] text-text-muted">sold steadily, then zero for a month while ≥2 branches kept selling (last 6 months)</span>
              </div>
              <DataTable columns={stockoutColumns} data={stockouts} isLoading={isLoading} onRowClick={(row) => openSku(row.skuId)} />
            </div>
          </div>
        )}
      </div>

      <SkuSheet skuId={selectedSku} open={sheetOpen} onOpenChange={setSheetOpen} demo={demo} mask={mask} />
    </div>
  )
}
